import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LiveStreamProvider } from "./context/LiveStreamContext";
import { native } from "./lib/native";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Markets from "./pages/Markets";
import AssetDetail from "./pages/AssetDetail";
import Portfolio from "./pages/Portfolio";
import Watchlist from "./pages/Watchlist";
import Orders from "./pages/Orders";
import TradeHistory from "./pages/TradeHistory";
import IPOs from "./pages/IPOs";
import IBKRBoard from "./pages/IBKRBoard";
import MarketTrends from "./pages/MarketTrends";
import AIInsights from "./pages/AIInsights";
import Settings from "./pages/Settings";
import IBKRConsent from "./pages/IBKRConsent";
import Bots from "./pages/Bots";
import Backtest from "./pages/Backtest";
import Options from "./pages/Options";
import Alerts from "./pages/Alerts";
import Leaderboard from "./pages/Leaderboard";
import RiskGuardrails from "./pages/RiskGuardrails";
import CopyTraders from "./pages/CopyTraders";
import BotPostmortem from "./pages/BotPostmortem";
import BotDashboard from "./pages/BotDashboard";
import AdvancedOrders from "./pages/AdvancedOrders";
import Strategies from "./pages/Strategies";
import Earnings from "./pages/Earnings";
import RealMoneyOnboarding from "./pages/RealMoneyOnboarding";
import Landing from "./pages/Landing";
import Help from "./pages/Help";
import Manual from "./pages/Manual";
import RiskSettings from "./pages/RiskSettings";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SecurityCenter from "./pages/SecurityCenter";
import Referrals from "./pages/Referrals";
import Setups from "./pages/Setups";
import Pricing from "./pages/Pricing";
import Founder from "./pages/Founder";
import PWANudge from "./components/PWANudge";
import { Toaster } from "./components/ui/sonner";
import "./App.css";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function App() {
  // Native mobile bootstrap — theme status bar + hide splash + register push
  useEffect(() => {
    native.setStatusBarColor("#0A0A0B", true);
    native.hideSplash();
    if (native.isNative) {
      native.registerPush((token) => {
        import("./lib/api").then(({ api }) => {
          api.post("/push/subscribe", {
            endpoint: `native://${native.platform}/${token}`,
            keys: { p256dh: "native", auth: "native" },
          }).catch(() => { /* silent — may not be logged in yet */ });
        });
      });
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/landing" element={<Landing />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route element={<Protected><LiveStreamProvider><Layout /></LiveStreamProvider></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/markets" element={<Markets />} />
              <Route path="/asset/:assetType/:symbol" element={<AssetDetail />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/watchlist" element={<Watchlist />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/history" element={<TradeHistory />} />
              <Route path="/ipos" element={<IPOs />} />
              <Route path="/ibkr" element={<IBKRBoard />} />
              <Route path="/trends" element={<MarketTrends />} />
              <Route path="/ai" element={<AIInsights />} />
              <Route path="/bots" element={<Bots />} />
              <Route path="/backtest" element={<Backtest />} />
              <Route path="/options" element={<Options />} />
              <Route path="/options/:symbol" element={<Options />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/security" element={<SecurityCenter />} />
              <Route path="/alerts" element={<Alerts />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/copy" element={<CopyTraders />} />
              <Route path="/earnings" element={<Earnings />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/setups" element={<Setups />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/founder" element={<Founder />} />
              <Route path="/help" element={<Help />} />
              <Route path="/manual" element={<Manual />} />
              <Route path="/risk-settings" element={<RiskSettings />} />
              <Route path="/risk" element={<RiskGuardrails />} />
              <Route path="/go-live" element={<RealMoneyOnboarding />} />
              <Route path="/strategies" element={<Strategies />} />
              <Route path="/advanced-orders" element={<AdvancedOrders />} />
              <Route path="/bots/:botId/postmortem" element={<BotPostmortem />} />
              <Route path="/bots/:botId" element={<BotDashboard />} />
              <Route path="/ibkr-consent" element={<IBKRConsent />} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
          <PWANudge />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
